import { css, cx } from "@emotion/css";
import { Folder } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CustomBadge } from "src/components/Default/CustomBadge";
import {
	OutlinedTextBlackButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import Box from "src/components/Default/Main/Box";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";

interface DefaultCreatorProps {
	onSubmit: (name: string, folderIds: string[]) => void;
	renderFilters: () => React.ReactNode;
	counterText: string;
	count: number;
	reset: () => void;
	defaultName?: string;
	name?: string | null;
	onNameChange?: (name: string) => void;
	inputPlaceholder?: string;
	inputLabel?: string;
	isLoading?: boolean;
	disabled?: boolean;
	className?: string;
	Header?: React.ReactNode;
	getPrimaryButtonText?: () => string;
	renderSecondaryButton?: () => React.ReactNode;
	initialSelectedFolderIds?: string[];
}

export function DefaultCreator(props: DefaultCreatorProps) {
	const { t } = useTranslation();
	const { openModal } = useModals();
	const [internalName, setInternalName] = useState<string | null>(null);
	const name = props.name !== undefined ? props.name : internalName;
	const setName: (value: string) => void =
		props.onNameChange ?? setInternalName;
	const [showTitleError, setShowTitleError] = useState(false);
	const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>(
		props.initialSelectedFolderIds ?? [],
	);

	const renderCounter = () => {
		return (
			<div className="creator--header-counter">
				<button
					type="button"
					className="link-button size-18"
					onClick={props.reset}
				>
					{t("creators.exams.removeFilters")}
				</button>
				<span className="size-18-light" data-test-id="creator-question-count">
					{props.counterText}: {props.count}
				</span>
			</div>
		);
	};

	const getPrimaryButtonText = () => {
		if (props.getPrimaryButtonText) {
			return props.getPrimaryButtonText();
		}

		return props.isLoading
			? t("creators.exams.creating")
			: `${t("commands.create")}`;
	};

	const getName = () => {
		if (name === null) {
			return props.defaultName ?? "";
		}

		return name;
	};

	const openFolderModal = () => {
		openModal(ModalsNames.SelectFoldersModal, {
			initialSelectedIds: selectedFolderIds,
			onSelect: (ids: unknown) => setSelectedFolderIds(ids as string[]),
		});
	};

	const renderFooter = () => {
		return (
			<div className="sticky-bottom">
				{props.renderSecondaryButton?.()}
				<PrimaryButton
					className="w-100"
					data-test-id="exam-save-button"
					onClick={() => {
						if (!getName()) {
							setShowTitleError(true);
							toast.error(t("errors.emptyName"), {
								position: toast.POSITION.BOTTOM_LEFT,
								pauseOnHover: false,
								autoClose: 2000,
							});
							return;
						}
						setShowTitleError(false);
						return props.onSubmit(getName(), selectedFolderIds);
					}}
					isLoading={props.isLoading}
					disabled={props.isLoading || props.disabled}
				>
					{getPrimaryButtonText()}
				</PrimaryButton>
			</div>
		);
	};

	return (
		<div className={cx("h-100", props.className)}>
			<Box className="creator--box">
				<div className="creator">
					<div className="creator--header">
						<div className="flex justify-between items-center">
							{props.Header}
							<div className="creator--counter-desktop">{renderCounter()}</div>
						</div>
						<div className="flex items-end gap-12">
							<div className="flex flex-col gap-4 flex-1">
								<span className="size-16-light">
									{props.inputLabel || t("creators.exams.examName")}
								</span>
								<Tooltip title={getName()}>
									<TextFieldGrey
										variant="filled"
										className={cx("w-100", css`max-width: 450px;`)}
										placeholder={props.inputPlaceholder}
										onChange={(e) => {
											setName(e.target.value);
											setShowTitleError(false);
										}}
										value={getName()}
										error={showTitleError}
										helperText={
											showTitleError ? t("errors.emptyName") : undefined
										}
										slotProps={{
											htmlInput: { "data-test-id": "exam-title-input" },
										}}
									/>
								</Tooltip>
							</div>
							<OutlinedTextBlackButton
								data-test-id="creator-select-folders-button"
								onClick={openFolderModal}
								className="flex items-center gap-8 w-fit-content creator--folder-button"
							>
								<div className="creator--folder-icon-wrapper">
									<Folder className="icon-small" />
									{selectedFolderIds.length > 0 && (
										<CustomBadge
											count={selectedFolderIds.length}
											maxCount={99}
											className="creator--folder-button-badge"
										/>
									)}
								</div>
								<span className="creator--folder-button-text">
									{selectedFolderIds.length > 0 ? (
										<>
											{t("creators.foldersSelectedLabel", "Foldery:")}{" "}
											<strong>{selectedFolderIds.length}</strong>
										</>
									) : (
										t("creators.selectFolders", "Dodaj do folderów")
									)}
								</span>
							</OutlinedTextBlackButton>
						</div>
						<div className="creator--counter-mobile">{renderCounter()}</div>
					</div>
					<div className="flex-1">
						<div>{props.renderFilters()}</div>
					</div>
					{renderFooter()}
				</div>
			</Box>
		</div>
	);
}

export default DefaultCreator;
